// src/stores/conversationStore.ts
import { create } from "zustand";
import {
  collection,
  doc,
  query,
  orderBy,
  limit,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../services/firebase";
import {
  clearLastConversationId,
  getLastConversationId,
} from "../services/lastConversation";

export type ConversationType =
  | 'standard'
  | 'walking_tour'
  | 'museum_guide'
  | 'map_exploration';

// To add a new conversation mode (e.g. 'food_crawl'):
//   1. Add a literal to ConversationType above.
//   2. Define <Mode>Data + any sub-types here.
//   3. Add an optional `<mode>` field on Conversation below.
//   4. Map the snapshot in startConversationListener so Timestamps
//      become Dates.
//   5. Add a Cloud Function that writes `{ type: '<mode>', <mode>: {...} }`.
//   6. Add a system-prompt branch in streamTourGuide so chat answers
//      adapt to the mode's state.
//   7. Add a top-half view component + a "Start <mode>" CTA in ChatInterface.

export type WalkingTourWaypointStatus =
  | 'pending'
  | 'current'
  | 'visited'
  | 'skipped';

export interface WalkingTourWaypoint {
  index: number;
  title: string;
  description: string;
  why_visit: string;
  walking_notes: string;
  lat: number;
  lng: number;
  status: WalkingTourWaypointStatus;
  arrived_at?: Date | null;
}

export interface WalkingTourData {
  status: 'generating' | 'ready' | 'error';
  /** Monotonic counter bumped by the server each time a (re)generation
   *  is requested. Part of the client trigger's dedup key so that
   *  re-picking a PREVIOUS focus still fires a fresh generation. */
  generation_seq?: number;
  title?: string;
  summary?: string;
  focus?: string | null;
  center?: { lat: number; lng: number };
  total_distance_m?: number;
  waypoints?: WalkingTourWaypoint[];
  current_waypoint_index?: number;
  started_at?: Date | null;
  completed_at?: Date | null;
  last_error?: string;
}

// ── Museum guide mode ────────────────────────────────────────────────
//
// Indoor cousin of walking_tour. Two sub-modes:
//   • 'guided'    — AI generates an ordered list of key exhibits the
//                   user works through, similar to walking_tour
//                   waypoints but without coordinates (museums are
//                   indoor; we use textual hints instead).
//   • 'free_roam' — no list, photo-driven. The user wanders and snaps
//                   photos of whatever catches their eye; the chat AI
//                   identifies the work and provides context. We just
//                   track an observation counter for stats display.

export type MuseumGuideMode = 'guided' | 'free_roam';

export type MuseumGuideExhibitStatus =
  | 'pending'
  | 'current'
  | 'visited'
  | 'skipped';

export interface MuseumGuideExhibit {
  index: number;
  title: string;
  description: string;
  why_visit: string;
  /** Free-text wayfinding hint within the museum
   *  (e.g. "second floor, west wing — past the gift shop"). */
  hint: string;
  status: MuseumGuideExhibitStatus;
  visited_at?: Date | null;
}

export interface MuseumGuideData {
  status:
    | 'awaiting_museum'
    | 'awaiting_submode'
    | 'generating'
    | 'ready'
    | 'error';
  /** Sub-mode is only known after the user picks (or the AI captures
   *  it via the state machine). Optional during the awaiting_* phases. */
  mode?: MuseumGuideMode;
  museum_name: string;
  museum_address?: string;
  /** Guided-only: thematic narrowing the user provided
   *  ("modern art only", "kid-friendly route"). */
  focus?: string | null;
  /** Guided-only: ordered exhibit list. */
  exhibits?: MuseumGuideExhibit[];
  /** Guided-only: which exhibit is "current". */
  current_exhibit_index?: number;
  started_at?: Date | null;
  completed_at?: Date | null;
  /** Free-roam: number of photos submitted in this session. */
  observations_count?: number;
  last_error?: string;
}

// ── Map exploration mode ─────────────────────────────────────────────
//
// Conversation-as-map-layer. The user types a prompt ("ramen near
// me", "art nouveau cafés") and the conversation is created with
// type='map_exploration'. The cloud function generates a ranked
// list of places that's persisted on the conversation doc. Subsequent
// turns refine the list (Phase 2) or answer freeform questions about
// the loaded places (Phase 2, via streamTourGuide mode branch).
//
// Why a conversation: layers ARE conversations. Sharing, deletion,
// rename, and persistence all come for free from the existing
// conversation infrastructure. See MAP_NOTES.md.

export interface MapExplorationFilters {
  /** Drop places below this Google rating. Default from
   *  user.preferences.mapMinRating, fallback 3.0. */
  min_rating: number;
  /** Drop places below this many reviews. Default from
   *  user.preferences.mapMinReviews, fallback 10. */
  min_reviews: number;
  /** Search/display radius in meters. Default from
   *  user.preferences.mapDefaultRadiusM, fallback 1500. */
  max_distance_m: number;
  /** Hide places that are explicitly closed right now. Computed on
   *  the client from cached opening_hours_periods — no API call
   *  inside the cache window. */
  open_now: boolean;
  /** Optional: 1..4 (Google's price levels). Empty/undefined means
   *  any. */
  price_levels?: number[];
}

export interface MapExplorationPlace {
  /** Google place_id. */
  place_id: string;
  name: string;
  lat: number;
  lng: number;
  rating?: number;
  user_ratings_total?: number;
  /** Google Places types — used for category filtering and pin
   *  fallback color when no photo is available. */
  types?: string[];
  /** 0..4 (free..very_expensive) when known. */
  price_level?: number;
  /** Photo reference for `placePhotoProxy`. The client never sees
   *  raw Places URLs / API keys; thumbnails are proxied. */
  photo_ref?: string;
  /** Cached `opening_hours.periods` array. Lets the client compute
   *  open_now without hitting Place Details inside the cache
   *  window. Schema is Google's: `{ open: { day, time }, close: {
   *  day, time } }`. May be absent for places without hours data. */
  opening_hours_periods?: Array<{
    open?: { day?: number; time?: string };
    close?: { day?: number; time?: string };
  }>;
  /** Final relevance for THIS user's query in [0,1]. Combines AI
   *  ranking (taste + conversation context fit) and Google's
   *  popularity signals. Drives sort order on the map and in any
   *  list view. */
  relevance: number;
  /** One-line guide blurb in the user's language. Reason this
   *  place is on the map. */
  blurb: string;
}

export interface MapExplorationData {
  /** generating  → cloud function is running.
   *  ready       → places[] is current.
   *  error       → last_error has the reason; UI shows retry. */
  status: 'generating' | 'ready' | 'error';
  /** Monotonic counter bumped by the server on each refinement.
   *  Part of the client trigger's dedup key — see WalkingTourData. */
  generation_seq?: number;
  /** The places to render as pins. Sorted by relevance. */
  places: MapExplorationPlace[];
  /** Filter state. Defaults populated from user.preferences when
   *  the conversation is created. */
  filters: MapExplorationFilters;
  /** The most recent user prompt — populated on creation and on
   *  every refinement. Surfaced as the layer drawer subtitle. */
  last_query: string;
  last_generated_at?: Date | null;
  last_error?: string;
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  created_at: Date;
  updated_at: Date;
  message_count: number;
  location_data: string[];
  is_public: boolean;
  tags: string[];
  last_message?: string;
  /** Conversation kind. Defaults to 'standard'. */
  type?: ConversationType;
  /** Populated only when type === 'walking_tour'. */
  walking_tour?: WalkingTourData;
  /** Populated only when type === 'museum_guide'. */
  museum_guide?: MuseumGuideData;
  /** Populated only when type === 'map_exploration'. */
  map_exploration?: MapExplorationData;
}

interface ConversationState {
  conversations: Conversation[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  unsubscribeListener: (() => void) | null;

  // Actions
  createConversation: (
    firstMessage?: string,
    location?: any,
    /** Optional initial mode + seed data, set when the user has
     *  picked a mode on the WelcomeScreen and is committing it via
     *  their first message. The seed data lands on the `<mode>`
     *  subdoc (e.g. museum_guide.status:'awaiting_museum'). When
     *  omitted, the conversation is created without a `type` field
     *  and falls back to 'standard'. */
    initialMode?: ConversationType,
    initialModeData?: Record<string, any>
  ) => Promise<string>;
  updateConversationMetadata: (
    conversationId: string,
    updates: Partial<Conversation>
  ) => Promise<void>;
  renameConversation: (
    conversationId: string,
    newTitle: string
  ) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  generateConversationTitle: (firstMessage: string) => string;
  startConversationListener: () => void;
  cleanupListener: () => void;
  clearError: () => void;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  conversations: [],
  isLoading: false,
  isCreating: false,
  error: null,
  unsubscribeListener: null,

  // Start real-time listener for conversations
  startConversationListener: () => {
    const user = auth.currentUser;
    if (!user) {
      console.log("No user for conversation listener");
      return;
    }

    // Only set up listener for registered users
    if (user.isAnonymous) {
      console.log("Anonymous user - skipping conversation listener");
      return;
    }

    // Clean up existing listener
    const currentUnsubscribe = get().unsubscribeListener;
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    console.log("Setting up conversation listener for user:", user.uid);
    set({ isLoading: true, error: null });

    const conversationsRef = collection(db, "conversations");
    const q = query(
      conversationsRef,
      where("user_id", "==", user.uid),
      orderBy("updated_at", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log("Conversations snapshot received, size:", snapshot.size);
        const conversations: Conversation[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          // Map walking_tour subdoc — Firestore Timestamps need
          // converting to Date for the store.
          let walkingTour: any = undefined;
          const wt = data.walking_tour;
          if (wt && typeof wt === 'object') {
            const waypoints = Array.isArray(wt.waypoints)
              ? wt.waypoints.map((w: any) => ({
                  ...w,
                  arrived_at:
                    w.arrived_at instanceof Timestamp
                      ? w.arrived_at.toDate()
                      : null,
                }))
              : [];
            walkingTour = {
              status: wt.status || 'ready',
              generation_seq:
                typeof wt.generation_seq === 'number'
                  ? wt.generation_seq
                  : 0,
              title: wt.title,
              summary: wt.summary,
              focus: wt.focus ?? null,
              center: wt.center,
              total_distance_m: wt.total_distance_m,
              waypoints,
              current_waypoint_index:
                typeof wt.current_waypoint_index === 'number'
                  ? wt.current_waypoint_index
                  : 0,
              started_at:
                wt.started_at instanceof Timestamp
                  ? wt.started_at.toDate()
                  : null,
              completed_at:
                wt.completed_at instanceof Timestamp
                  ? wt.completed_at.toDate()
                  : null,
              last_error: wt.last_error,
            };
          }

          // Map museum_guide subdoc. Same Timestamp→Date treatment as
          // walking_tour. Mode-specific fields stay optional —
          // free_roam doesn't carry exhibits, guided doesn't carry
          // observations_count.
          let museumGuide: any = undefined;
          const mg = data.museum_guide;
          if (mg && typeof mg === 'object') {
            const exhibits = Array.isArray(mg.exhibits)
              ? mg.exhibits.map((e: any) => ({
                  ...e,
                  visited_at:
                    e.visited_at instanceof Timestamp
                      ? e.visited_at.toDate()
                      : null,
                }))
              : [];
            museumGuide = {
              status: mg.status || 'ready',
              // Mode stays undefined until the user picks. Only set
              // when explicitly stored as 'guided' or 'free_roam' —
              // anything else (including absent) leaves the field
              // unset so the UI can tell "not picked yet" from
              // "picked guided".
              mode:
                mg.mode === 'guided' || mg.mode === 'free_roam'
                  ? mg.mode
                  : undefined,
              museum_name: mg.museum_name || '',
              museum_address: mg.museum_address,
              focus: mg.focus ?? null,
              exhibits,
              current_exhibit_index:
                typeof mg.current_exhibit_index === 'number'
                  ? mg.current_exhibit_index
                  : 0,
              started_at:
                mg.started_at instanceof Timestamp
                  ? mg.started_at.toDate()
                  : null,
              completed_at:
                mg.completed_at instanceof Timestamp
                  ? mg.completed_at.toDate()
                  : null,
              observations_count:
                typeof mg.observations_count === 'number'
                  ? mg.observations_count
                  : 0,
              last_error: mg.last_error,
            };
          }

          // Map map_exploration subdoc — same Timestamp→Date
          // hygiene as the other modes. The places array doesn't
          // carry timestamps today (last_generated_at is on the
          // parent), but we still build a fresh object so the type
          // is exact and we don't leak Firestore Timestamp shapes
          // into store consumers.
          let mapExploration: any = undefined;
          const me = data.map_exploration;
          if (me && typeof me === 'object') {
            const places = Array.isArray(me.places)
              ? me.places
                  .filter(
                    (p: any) =>
                      p &&
                      typeof p.place_id === 'string' &&
                      typeof p.lat === 'number' &&
                      typeof p.lng === 'number'
                  )
                  .map((p: any) => ({
                    place_id: p.place_id,
                    name: typeof p.name === 'string' ? p.name : '',
                    lat: p.lat,
                    lng: p.lng,
                    rating:
                      typeof p.rating === 'number' ? p.rating : undefined,
                    user_ratings_total:
                      typeof p.user_ratings_total === 'number'
                        ? p.user_ratings_total
                        : undefined,
                    types: Array.isArray(p.types) ? p.types : undefined,
                    price_level:
                      typeof p.price_level === 'number'
                        ? p.price_level
                        : undefined,
                    photo_ref:
                      typeof p.photo_ref === 'string'
                        ? p.photo_ref
                        : undefined,
                    opening_hours_periods: Array.isArray(
                      p.opening_hours_periods
                    )
                      ? p.opening_hours_periods
                      : undefined,
                    relevance:
                      typeof p.relevance === 'number' ? p.relevance : 0,
                    blurb: typeof p.blurb === 'string' ? p.blurb : '',
                  }))
              : [];
            const f = me.filters || {};
            const filters: MapExplorationFilters = {
              min_rating:
                typeof f.min_rating === 'number' ? f.min_rating : 3.0,
              min_reviews:
                typeof f.min_reviews === 'number' ? f.min_reviews : 10,
              max_distance_m:
                typeof f.max_distance_m === 'number'
                  ? f.max_distance_m
                  : 1500,
              open_now: f.open_now === true,
              price_levels: Array.isArray(f.price_levels)
                ? f.price_levels
                : undefined,
            };
            mapExploration = {
              status: me.status || 'ready',
              generation_seq:
                typeof me.generation_seq === 'number'
                  ? me.generation_seq
                  : 0,
              places,
              filters,
              last_query:
                typeof me.last_query === 'string' ? me.last_query : '',
              last_generated_at:
                me.last_generated_at instanceof Timestamp
                  ? me.last_generated_at.toDate()
                  : null,
              last_error: me.last_error,
            };
          }

          // Resolve discriminated type. Anything we don't recognize
          // (legacy or future-rolled-back values) falls back to
          // 'standard' so the chat still renders.
          const rawType = data.type;
          const resolvedType: ConversationType =
            rawType === 'walking_tour' ||
            rawType === 'museum_guide' ||
            rawType === 'map_exploration'
              ? rawType
              : 'standard';

          conversations.push({
            id: doc.id,
            user_id: data.user_id,
            title: data.title || "Untitled Conversation",
            created_at:
              data.created_at instanceof Timestamp
                ? data.created_at.toDate()
                : new Date(data.created_at),
            updated_at:
              data.updated_at instanceof Timestamp
                ? data.updated_at.toDate()
                : new Date(data.updated_at),
            message_count: data.message_count || 0,
            location_data: data.location_data || [],
            is_public: data.is_public || false,
            tags: data.tags || [],
            last_message: data.last_message || undefined,
            type: resolvedType,
            walking_tour: walkingTour,
            museum_guide: museumGuide,
            map_exploration: mapExploration,
          });
        });

        console.log("Processed conversations:", conversations.length);
        set({
          conversations,
          isLoading: false,
          unsubscribeListener: unsubscribe,
        });
      },
      (error) => {
        console.error("Conversation listener error:", error);
        set({
          error: error.message,
          isLoading: false,
        });
      }
    );
  },

  // Lazy conversation creation - only when first message is sent
  createConversation: async (
    firstMessage?: string,
    location?: any,
    initialMode?: ConversationType,
    initialModeData?: Record<string, any>
  ) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user authenticated");
    }

    try {
      set({ isCreating: true, error: null });

      // Generate conversation ID
      const conversationId = `conv_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`;

      // Generate title from first message or use default
      const title = firstMessage
        ? get().generateConversationTitle(firstMessage)
        : "New Conversation";

      const conversationData: any = {
        user_id: user.uid,
        title,
        created_at: new Date(),
        updated_at: new Date(),
        message_count: firstMessage ? 1 : 0,
        location_data: [],
        is_public: false,
        tags: [],
      };

      // Seed mode + subdoc when the caller picked a non-standard mode
      // on the WelcomeScreen. We don't write a `type` field for
      // 'standard' — the snapshot mapper falls back to 'standard'
      // when type is absent, so leaving it out keeps existing data
      // shape unchanged for the common case.
      if (initialMode && initialMode !== 'standard') {
        conversationData.type = initialMode;
        if (initialMode === 'walking_tour' && initialModeData) {
          conversationData.walking_tour = initialModeData;
        } else if (initialMode === 'museum_guide' && initialModeData) {
          conversationData.museum_guide = initialModeData;
        } else if (initialMode === 'map_exploration' && initialModeData) {
          conversationData.map_exploration = initialModeData;
        }
      }

      // Only add location_data if location exists and has valid geohash
      if (
        location &&
        location.geohash &&
        typeof location.geohash === "string"
      ) {
        conversationData.location_data = [location.geohash];
      }

      // Add last_message if we have one
      if (firstMessage) {
        conversationData.last_message =
          firstMessage.length > 100
            ? firstMessage.substring(0, 100) + "..."
            : firstMessage;
      }

      // Clean any undefined values before saving
      const cleanedConversationData = Object.fromEntries(
        Object.entries(conversationData).filter(
          ([_, value]) => value !== undefined
        )
      );

      console.log(
        "Creating conversation:",
        conversationId,
        cleanedConversationData
      );

      // Use batch write for atomicity if we're also creating a message
      if (firstMessage) {
        const batch = writeBatch(db);

        // Add conversation document
        const conversationRef = doc(db, "conversations", conversationId);
        batch.set(conversationRef, cleanedConversationData);

        await batch.commit();
      } else {
        // Just create the conversation document
        await setDoc(
          doc(db, "conversations", conversationId),
          cleanedConversationData
        );
      }

      set({ isCreating: false });

      console.log("Conversation created successfully:", conversationId);
      return conversationId;
    } catch (error) {
      console.error("Error creating conversation:", error);
      set({
        error: (error as Error).message,
        isCreating: false,
      });
      throw error;
    }
  },

  // Update conversation metadata when messages are added
  updateConversationMetadata: async (
    conversationId: string,
    updates: Partial<Conversation>
  ) => {
    try {
      const updateData = {
        ...updates,
        updated_at: new Date(),
      };

      // Clean undefined values
      const cleanedData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      console.log(
        "Updating conversation metadata:",
        conversationId,
        cleanedData
      );
      await updateDoc(doc(db, "conversations", conversationId), cleanedData);
    } catch (error) {
      console.error("Error updating conversation metadata:", error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  // Rename conversation title
  renameConversation: async (conversationId: string, newTitle: string) => {
    const user = auth.currentUser;
    if (!user) {
      set({ error: "No user authenticated" });
      return;
    }

    const title = newTitle.trim();
    if (!title) {
      return;
    }

    try {
      await updateDoc(doc(db, "conversations", conversationId), {
        title,
        updated_at: new Date(),
      });
      console.log("Conversation renamed:", conversationId, title);
    } catch (error) {
      console.error("Error renaming conversation:", error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  // Delete conversation and all its messages
  deleteConversation: async (conversationId: string) => {
    const user = auth.currentUser;
    if (!user) {
      set({ error: "No user authenticated" });
      return;
    }

    try {
      // Note: In production, you'd want to use a Cloud Function to delete
      // all messages in the subcollection as well
      await deleteDoc(doc(db, "conversations", conversationId));

      // If the deleted conversation was the one we'd resume on next
      // load, clear it. Without this, the next session redirects to
      // /chat/<deletedId>, the messages-subcollection rule denies the
      // first send (`get()` on the missing parent), and the user gets
      // "missing or insufficient permissions" until they click New
      // Chat. Only clear when it matches — we don't want to wipe a
      // valid resume target just because some other conversation got
      // deleted.
      const last = getLastConversationId(user.uid);
      if (last === conversationId) {
        clearLastConversationId(user.uid);
      }

      console.log("Conversation deleted:", conversationId);
    } catch (error) {
      console.error("Error deleting conversation:", error);
      set({ error: (error as Error).message });
    }
  },

  // Generate conversation title from first message
  generateConversationTitle: (firstMessage: string) => {
    // Extract meaningful words and create a title
    const words = firstMessage
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .split(/\s+/)
      .filter(
        (word) =>
          word.length > 2 &&
          ![
            "the",
            "and",
            "but",
            "for",
            "are",
            "you",
            "can",
            "what",
            "how",
            "where",
            "when",
            "why",
          ].includes(word)
      );

    if (words.length === 0) {
      return "New Conversation";
    }

    // Take first 3-4 meaningful words
    const titleWords = words.slice(0, 4);
    let title = titleWords
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    // Limit length
    if (title.length > 40) {
      title = title.substring(0, 37) + "...";
    }

    return title || "New Conversation";
  },

  // Clean up listener when component unmounts or user changes
  cleanupListener: () => {
    const unsubscribe = get().unsubscribeListener;
    if (unsubscribe) {
      console.log("Cleaning up conversation listener");
      unsubscribe();
      set({ unsubscribeListener: null });
    }
  },

  clearError: () => set({ error: null }),
}));
